import React from 'react';

import {
    getSquarespaceBrief,
    getSquarespaceBriefVariables,
} from '__generated__/getSquarespaceBrief';
import { SquarespaceBriefFields } from '__generated__/SquarespaceBriefFields';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import { BriefStep, StepState } from 'projects/util/step';
import { useRouteMatch } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import DetailsPage from './DetailsPage';
import queries from './SquarespaceBrief.graphql';
import SummaryPage from './SummaryPage';

interface RouteProps {
    briefId: string;
    step: string;
}

const steps: BriefStep<SquarespaceBriefFields>[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
        fields: DetailsPage.fields(),
    },
    {
        step: 2,
        title: __('Review your brief'),
    },
    {
        step: 3,
        title: __('Invite designers to quote'),
        isAuthRequired: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export default function SquarespaceBrief() {
    const route = useRouteMatch<RouteProps>();

    return (
        <BriefQuery<getSquarespaceBrief, getSquarespaceBriefVariables>
            query={queries.getSquarespaceBrief}
            variables={{ id: route!.params.briefId }}
            fieldsTypename="SquarespaceBriefFields"
        >
            {(data, refetch) => {
                const brief = {
                    ...data.brief,
                    fields: data.brief.fields as SquarespaceBriefFields,
                };

                const step = new StepState({
                    steps,
                    brief: data.brief,
                    currentStep: parseInt(route!.params.step, 10),
                });

                return (
                    <Brief
                        refetch={refetch}
                        schema="squarespace"
                        brief={brief}
                        viewer={data.viewer}
                        stepState={step}
                    >
                        {form => (
                            <Switch>
                                <Route
                                    path={`/briefs/squarespace/:briefId/1`}
                                    render={() => <DetailsPage brief={brief} form={form} />}
                                />
                                <Route
                                    path={`/briefs/squarespace/:briefId/2`}
                                    render={() => <SummaryPage brief={brief} />}
                                />
                                <Route
                                    path={`/briefs/squarespace/:briefId/3`}
                                    render={() => (
                                        <InviteDesignersPage
                                            brief={brief}
                                            viewer={data.viewer}
                                            description={__(
                                                "We've matched you with these awesome designers. Invite up to 5 to quote."
                                            )}
                                        />
                                    )}
                                />
                            </Switch>
                        )}
                    </Brief>
                );
            }}
        </BriefQuery>
    );
}
